import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../store/actions/userActions";
import wordmark from "../../../assets/brand/cache-wordmark.svg";
import { Avatar } from "../../ForBrands/ui/BrandUI";
import { useFitScale, fitScaleStyle } from "../../ForBrands/useFitScale";
import "../../ForBrands/brandMode.css";

// The supporter shell (parents, guardians and trainers): the V2 72px
// topbar and tab bar, as in the rep and coach shells, with the SUPPORTER
// label and the My Circle tab, which stays active in the athlete editor
// opened from it (/supporter/athlete/edit). App.js keeps the global Navbar
// off every /supporter route.
export const SUPPORTER_HOME = "/supporter/circle";
const SUPPORTER_ATHLETE_EDIT = "/supporter/athlete/edit";
const TABS = [{ path: SUPPORTER_HOME, label: "My Circle", match: [SUPPORTER_HOME, SUPPORTER_ATHLETE_EDIT] }];
const PAD = "px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]";

const nameOf = (user) => [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.name || user?.email || "Supporter";

const AccountMenu = ({ user }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const name = nameOf(user);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const signOut = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label="Account" className="flex items-center gap-4">
        <span className="hidden bm-label-l text-bm-ink sm:inline">{name}</span>
        <Avatar src={user?.avatar} name={user?.firstName || name} surname={user?.lastName} size={32} tone="brand" />
      </button>
      {open && (
        <div role="menu" className="bm-fade-in absolute right-0 top-11 z-40 w-60 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl">
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
          </div>
          <div className="border-t border-bm-border" />
          <Link to="/supporter/password" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">Password</Link>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={signOut} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">Log out</button>
        </div>
      )}
    </div>
  );
};

const SupporterModeLayout = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user?.user || {});
  const fit = useFitScale();

  return (
    <div className="bm-root bg-white" style={fitScaleStyle(fit)}>
      <header className="bg-white">
        <div className={`flex h-[72px] w-full items-center justify-between ${PAD}`}>
          <Link to={SUPPORTER_HOME} className="flex items-center" aria-label="Supporter home">
            <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden bm-overline md:inline">Supporter</span>
            <AccountMenu user={user} />
          </div>
        </div>
        <nav className="border-b border-bm-border">
          <div className={`flex h-[51px] w-full items-stretch overflow-x-auto ${PAD}`}>
            {TABS.map((tab) => {
              const active = tab.match.some((m) => location.pathname.startsWith(m));
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  aria-current={active ? "page" : undefined}
                  className={`flex shrink-0 items-center px-6 font-archivo text-[15px] font-bold leading-5 ${active ? "border-b-2 border-bm-orange text-bm-orange" : "text-bm-muted hover:text-bm-ink"}`}
                >
                  {tab.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>
      <main className={`w-full pb-[70px] ${PAD}`}>
        <Outlet />
      </main>
    </div>
  );
};

export default SupporterModeLayout;
